// EN mirror of /terms — Terms of Service
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FileText } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { BRAND_URL } from "@/lib/brand";

const TITLE = "Terms of Service — Translation & Legalization Contract | NYC Language Institute";
const DESC =
  "Service terms for translation, notarization, apostille. Start with a free review before you file anything.";

export const Route = createFileRoute("/en/terms")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/terms", "en")},
      { name: "description", content: ctaDesc(DESC, "/en/terms", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/terms", "en")},
      { property: "og:description", content: ctaDesc(DESC, "/en/terms", "en")},
      { property: "og:url", content: `${BRAND_URL}/en/terms` },
      { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${BRAND_URL}/en/terms` },
      ...hreflangLinks("/terms", "/en/terms"),
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: TITLE, url: `${BRAND_URL}/en/terms`, inLanguage: "en",
      }),
    }],
  }),
  component: EnTerms,
});

const CLAUSES = [
  ["1. Scope", "These terms govern every quotation, work order, and delivery between NYC Language Institute Co., Ltd. (\"NYC\") and the client. Signing the quotation, replying \"confirm\" on LINE, or paying the deposit constitutes acceptance."],
  ["2. Turnaround", "Standard TAT: 1–3 business days for translation up to 5 pages, 5–7 business days for MFA legalization, 7–14 for embassy legalization. Same-day service available with 50% rush surcharge, subject to counter availability. Force-majeure delays (holidays, embassy closure, MFA system outage) suspend the clock."],
  ["3. Fees & payment", "Quotations are valid for 14 days. 50% deposit required before work starts on orders over THB 3,000. Balance due at delivery. Bank transfer (SCB, Kbank), PromptPay, or cash. Foreign wire transfers must include a 500 THB bank-fee buffer."],
  ["4. Accuracy standard", "Every translation is bilingual-QA'd against the source before certification. If a factual error (name, date, number) is found within 30 days, we recall, correct, and re-legalize at zero cost to you. Stylistic disagreements are not errors."],
  ["5. Client obligations", "You warrant that source documents are genuine, that you have the right to submit them, and that stated legal names / dates are correct. NYC is not liable for downstream rejection caused by forged, altered, or expired source documents."],
  ["6. Refund policy", "Full refund if we haven't started work (translator not assigned). 50% refund after assignment but before draft delivery. No refund after certification / notarization, because MFA and Lawyers-Council fees are non-refundable. Rejection by MFA / an embassy due to our error triggers redo + full refund of our service fee."],
  ["7. Liability cap", "Our aggregate liability for any claim is capped at three (3) times the fee paid for the specific work order. We are not liable for indirect, consequential, or lost-opportunity damages (missed visa deadline, lost job offer, etc.)."],
  ["8. Confidentiality", "All staff and freelancers sign an NDA on hire. Physical documents held in a fireproof safe. Digital files encrypted at rest. Confidentiality survives termination of the engagement."],
  ["9. Governing law & venue", "Thai law governs. Exclusive venue: the courts of Bangkok. Non-Thai clients waive the right to invoke foreign forum non conveniens. Consumer-protection remedies under Thai CPB Act B.E. 2522 are preserved for individual clients."],
  ["10. Amendments", "We publish updates 14 days before they take effect. Continued use after the effective date is deemed acceptance."],
];

function EnTerms() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-14 text-white">
        <div className="mx-auto max-w-4xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <div className="mt-3 flex items-center gap-3">
            <FileText className="h-8 w-8 text-amber-300" aria-hidden />
            <h1 className="text-3xl font-extrabold sm:text-4xl">Terms of Service</h1>
          </div>
          <p className="mt-3 max-w-2xl text-white/80">Version 2026-07-16 · Governed by Thai law · Bangkok jurisdiction.</p>
        </div>
      </section>
      <section className="px-4 py-12">
        <div className="mx-auto max-w-3xl space-y-5">
          {CLAUSES.map(([h, p]) => (
            <article key={h} className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="text-lg font-bold text-navy-deep">{h}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">{p}</p>
            </article>
          ))}
          <p className="text-xs text-slate-500">
            Thai version: <Link to="/terms" className="text-amber-700 underline">/terms</Link> · Questions: <a className="text-amber-700 underline" href="mailto:contact@nyclanguageinstitute.com">contact@nyclanguageinstitute.com</a>
          </p>
        </div>
      </section>
    <RelatedHubGrid locale="en" /></main>
  );
}
